import { useState, useMemo } from 'react';
import { demoBookings, demoGuests, demoRooms, demoRoomBeds } from '@/mocks/demoData';
import type { Booking, Room, Payment } from '@/mocks/data';
import { useApp } from '@/contexts/AppContext';
import { addPayment, generateReceiptNo, generatePaymentId, getPaymentsByBooking } from '@/utils/paymentStore';

export default function ReceptionCheckout() {
  const { addToast } = useApp();
  const [bookings, setBookings] = useState<Booking[]>(demoBookings);
  const [rooms, setRooms] = useState<Room[]>(demoRooms);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [showBill, setShowBill] = useState(false);
  const [collectedAmount, setCollectedAmount] = useState('');
  const [checkoutPaymentMethod, setCheckoutPaymentMethod] = useState<Payment['payment_method']>('cash');

  const checkedInBookings = useMemo(
    () => bookings.filter((b) => b.status === 'checked_in'),
    [bookings]
  );

  const getGuest = (id: string) => demoGuests.find((g) => g.id === id);
  const getRoom = (id: string) => rooms.find((r) => r.id === id);
  const getPayments = (id: string) => getPaymentsByBooking(id);

  const calculateBill = (booking: Booking) => {
    const room = getRoom(booking.room_id);
    if (!room) return null;

    const nights = Math.max(1, Math.ceil(
      (new Date(booking.check_out).getTime() - new Date(booking.check_in).getTime()) / (1000 * 60 * 60 * 24)
    ));

    const roomCharge = room.tariff * nights;
    const extraCharges = booking.extra_charges.reduce((sum, c) => sum + c.amount, 0);
    const subtotal = roomCharge + extraCharges;
    const cgst = subtotal * 0.06;
    const sgst = subtotal * 0.06;
    const totalWithTax = subtotal + cgst + sgst;
    const totalPayments = getPayments(booking.id).reduce((sum, p) => sum + p.amount, 0);
    const pending = totalWithTax - totalPayments;

    return {
      nights,
      roomCharge,
      extraCharges,
      subtotal,
      cgst,
      sgst,
      totalWithTax,
      totalPayments,
      pending,
      room,
      guest: getGuest(booking.guest_id),
    };
  };

  const handleShowBill = (booking: Booking) => {
    setSelectedBooking(booking);
    setShowBill(true);
    setCollectedAmount('');
  };

  const handleBackToList = () => {
    setShowBill(false);
    setSelectedBooking(null);
    setCollectedAmount('');
  };

  const handleCheckout = () => {
    if (!selectedBooking) return;

    const billForCheckout = calculateBill(selectedBooking);
    if (!billForCheckout) return;

    const collected = parseFloat(collectedAmount) || 0;
    const effectivePending = billForCheckout.pending - collected;

    if (effectivePending > 0) {
      addToast('error', 'Pending balance must be fully cleared before check-out');
      return;
    }

    // Create payment record if amount collected
    if (collected > 0) {
      const receiptNo = generateReceiptNo();
      const payment: Payment = {
        id: generatePaymentId(),
        booking_id: selectedBooking.id,
        amount: collected,
        payment_method: checkoutPaymentMethod,
        payment_type: 'full',
        receipt_no: receiptNo,
        created_by: 'usr-003',
        created_at: new Date().toISOString(),
      };
      addPayment(payment);
      addToast('success', `Receipt ${receiptNo} — ${checkoutPaymentMethod.toUpperCase()}`);
    }

    setBookings((prev) =>
      prev.map((b) =>
        b.id === selectedBooking.id
          ? { ...b, status: 'checked_out' as const, pending_amount: Math.max(0, effectivePending) }
          : b
      )
    );
    setRooms((prev) =>
      prev.map((r) =>
        r.id === selectedBooking.room_id ? { ...r, status: 'cleaning' as const } : r
      )
    );

    const room = getRoom(selectedBooking.room_id);
    addToast('success', `Checkout done! All payments cleared. Room ${room?.room_number} marked as Cleaning. Bill generated.`);

    setShowBill(false);
    setSelectedBooking(null);
    setCollectedAmount('');
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-navy-800">Guest Check-out</h1>
        <p className="text-navy-500 mt-1">Process check-outs with auto-generated bills.</p>
      </div>

      {!showBill ? (
        <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
          <div className="px-5 py-4 border-b border-navy-100">
            <h3 className="font-semibold text-navy-800">Currently Checked-in Guests ({checkedInBookings.length})</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-navy-50">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Guest</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Room</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Check-in</th>
                  <th className="text-left px-5 py-3 font-semibold text-navy-700">Check-out</th>
                  <th className="text-right px-5 py-3 font-semibold text-navy-700">Total</th>
                  <th className="text-right px-5 py-3 font-semibold text-navy-700">Pending</th>
                  <th className="text-center px-5 py-3 font-semibold text-navy-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {checkedInBookings.map((b) => {
                  const g = getGuest(b.guest_id);
                  const r = getRoom(b.room_id);
                  const bill = calculateBill(b);
                  return (
                    <tr key={b.id} className="border-t border-navy-100 hover:bg-navy-50/50 transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="font-medium text-navy-800">{g?.full_name || 'Unknown'}</div>
                        <div className="text-xs text-navy-400">{b.booking_id}</div>
                      </td>
                      <td className="px-5 py-3.5 text-navy-600">{r?.room_number} ({r?.category})</td>
                      <td className="px-5 py-3.5 text-navy-500">
                        {new Date(b.check_in).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-5 py-3.5 text-navy-500">
                        {new Date(b.check_out).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </td>
                      <td className="px-5 py-3.5 text-right font-medium text-navy-800">Rs. {bill?.totalWithTax.toLocaleString('en-IN') || '-'}</td>
                      <td className="px-5 py-3.5 text-right">
                        <span className={bill && bill.pending > 0 ? 'text-red-600 font-medium' : 'text-green-600 font-medium'}>
                          Rs. {bill?.pending.toLocaleString('en-IN') || '-'}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-center">
                        <button
                          onClick={() => handleShowBill(b)}
                          className="px-3 py-1.5 bg-navy-700 text-white rounded-md text-xs font-medium hover:bg-navy-800 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-bill-line mr-1" /> Generate Bill
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {checkedInBookings.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-5 py-12 text-center text-navy-400">
                      <i className="ri-checkbox-circle-line text-3xl mb-2 block" />
                      No checked-in guests at the moment.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : selectedBooking && (
        <div className="max-w-3xl mx-auto">
          <button
            onClick={handleBackToList}
            className="mb-4 flex items-center gap-2 text-sm text-navy-500 hover:text-navy-700 cursor-pointer"
          >
            <i className="ri-arrow-left-line" /> Back to list
          </button>

          {(() => {
            const bill = calculateBill(selectedBooking)!;
            const collected = parseFloat(collectedAmount) || 0;
            const effectivePending = Math.max(0, bill.pending - collected);
            const isFullyPaid = effectivePending === 0;

            return (
              <div className="bg-white rounded-xl border border-navy-100 overflow-hidden">
                {/* Hotel Header */}
                <div className="bg-navy-800 text-white px-8 py-6 text-center">
                  <div className="font-display text-2xl font-semibold">Hotel<span className="text-gold-400">Pro</span> Inn</div>
                  <div className="text-sm text-navy-300 mt-1">123 Hotel Road, Bengaluru, Karnataka - 560001</div>
                  <div className="text-sm text-navy-300">GSTIN: 29AABCU9603R1ZX | Phone: +91 80 1234 5678</div>
                </div>

                <div className="p-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-xl font-bold text-navy-800">GST Invoice</h2>
                      <div className="text-sm text-navy-500 mt-1">{selectedBooking.booking_id}</div>
                    </div>
                    <div className="text-right text-sm text-navy-500">
                      <div>Date: {new Date().toLocaleDateString('en-IN')}</div>
                      <div>Time: {new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</div>
                    </div>
                  </div>

                  {/* Guest Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-6 pb-6 border-b border-navy-100">
                    <div>
                      <div className="text-xs font-semibold text-navy-400 uppercase mb-2">Guest Details</div>
                      <div className="font-semibold text-navy-800">{bill.guest?.full_name}</div>
                      <div className="text-sm text-navy-500">{bill.guest?.phone}</div>
                      <div className="text-sm text-navy-500">{bill.guest?.email}</div>
                      <div className="text-sm text-navy-500 mt-1">{bill.guest?.id_proof_type}: {bill.guest?.id_proof_number}</div>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-navy-400 uppercase mb-2">Stay Details</div>
                      <div className="text-sm text-navy-600">Room: <span className="font-medium">{bill.room?.room_number}</span> ({bill.room?.category})</div>
                      {selectedBooking.is_bed_booking && selectedBooking.bed_ids.length > 0 && (
                        <div className="text-sm text-navy-600">
                          Beds: <span className="font-medium">
                            {selectedBooking.bed_ids.map((bid) => {
                              const b = demoRoomBeds.find((x) => x.id === bid);
                              return b ? `Bed ${b.bed_number}` : '';
                            }).filter(Boolean).join(', ')}
                          </span>
                        </div>
                      )}
                      <div className="text-sm text-navy-600">Check-in: {new Date(selectedBooking.check_in).toLocaleDateString('en-IN')}</div>
                      <div className="text-sm text-navy-600">Check-out: {new Date().toLocaleDateString('en-IN')}</div>
                      <div className="text-sm text-navy-600">Nights: {bill.nights}</div>
                    </div>
                  </div>

                  {/* Charges Table */}
                  <table className="w-full text-sm mb-6">
                    <thead className="bg-navy-50">
                      <tr>
                        <th className="text-left px-4 py-2 font-semibold text-navy-700">Item</th>
                        <th className="text-right px-4 py-2 font-semibold text-navy-700">Amount (Rs.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-navy-100">
                        <td className="px-4 py-3 text-navy-700">
                          Room Charges ({bill.nights} nights x Rs. {bill.room?.tariff.toLocaleString('en-IN')})
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{bill.roomCharge.toLocaleString('en-IN')}</td>
                      </tr>
                      {selectedBooking.extra_charges.map((c, idx) => (
                        <tr key={idx} className="border-t border-navy-100">
                          <td className="px-4 py-3 text-navy-700">{c.name}</td>
                          <td className="px-4 py-3 text-right font-medium">{c.amount.toLocaleString('en-IN')}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-navy-100 bg-navy-50/50">
                        <td className="px-4 py-3 font-medium text-navy-800">Subtotal</td>
                        <td className="px-4 py-3 text-right font-medium text-navy-800">{bill.subtotal.toLocaleString('en-IN')}</td>
                      </tr>
                      <tr className="border-t border-navy-100">
                        <td className="px-4 py-2 text-navy-500 text-xs">CGST @ 6%</td>
                        <td className="px-4 py-2 text-right text-navy-500 text-xs">{bill.cgst.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t border-navy-100">
                        <td className="px-4 py-2 text-navy-500 text-xs">SGST @ 6%</td>
                        <td className="px-4 py-2 text-right text-navy-500 text-xs">{bill.sgst.toFixed(2)}</td>
                      </tr>
                      <tr className="border-t-2 border-navy-300 bg-gold-50">
                        <td className="px-4 py-3 font-bold text-navy-900">Grand Total</td>
                        <td className="px-4 py-3 text-right font-bold text-navy-900 text-lg">Rs. {bill.totalWithTax.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </tbody>
                  </table>

                  {/* Payment Summary */}
                  <div className="bg-navy-50 rounded-lg p-5 mb-6">
                    <div className="text-sm font-semibold text-navy-700 mb-3">Payment Summary</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm mb-4">
                      <div className="text-navy-500">Total Payments Made</div>
                      <div className="text-right font-medium text-green-600">Rs. {bill.totalPayments.toLocaleString('en-IN')}</div>
                      <div className="text-navy-500">Pending Balance</div>
                      <div className={`text-right font-medium ${bill.pending > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        Rs. {bill.pending.toLocaleString('en-IN')}
                      </div>
                    </div>

                    {/* Balance > 0: Collect Payment Section */}
                    {bill.pending > 0 && (
                      <>
                        <div className="pt-3 border-t border-navy-200">
                          <div className="flex items-center gap-2 mb-2">
                            <i className="ri-money-rupee-circle-line text-navy-600" />
                            <span className="text-sm font-medium text-navy-700">Collect Payment Now</span>
                          </div>
                          <div className="flex gap-2">
                            <input
                              type="number"
                              className="flex-1 px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                              placeholder={`Pending: Rs. ${bill.pending.toLocaleString('en-IN')}`}
                              value={collectedAmount}
                              onChange={(e) => setCollectedAmount(e.target.value)}
                            />
                            <button
                              onClick={() => setCollectedAmount(bill.pending.toString())}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                            >
                              Pay Full
                            </button>
                          </div>
                          {collected > 0 && (
                            <div className="mt-3">
                              <label className="block text-xs font-medium text-navy-500 mb-1">Payment Method</label>
                              <select
                                className="w-full px-3 py-2 border border-navy-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500"
                                value={checkoutPaymentMethod}
                                onChange={(e) => setCheckoutPaymentMethod(e.target.value as Payment['payment_method'])}
                              >
                                <option value="cash">Cash</option>
                                <option value="card">Card</option>
                                <option value="upi">UPI</option>
                                <option value="online">Online</option>
                              </select>
                            </div>
                          )}
                        </div>

                        {/* Collection Feedback */}
                        {collected > 0 && collected < bill.pending && (
                          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 text-amber-700 text-sm">
                              <i className="ri-information-line" />
                              <span>
                                Rs. {collected.toLocaleString('en-IN')} collected. Still pending:{' '}
                                <strong>Rs. {effectivePending.toLocaleString('en-IN')}</strong>
                              </span>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {/* Fully Paid Feedback */}
                    {bill.pending > 0 && collected >= bill.pending && (
                      <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-green-700 text-sm">
                          <i className="ri-check-double-line" />
                          <span>Full payment collected! Balance cleared.</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleBackToList}
                      className="flex-1 px-4 py-2.5 border border-navy-200 rounded-lg text-sm font-medium text-navy-600 hover:bg-navy-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Cancel
                    </button>

                    {isFullyPaid ? (
                      <button
                        onClick={handleCheckout}
                        className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-check-double-line mr-2" />
                        Complete Check-out
                      </button>
                    ) : (
                      <button
                        disabled
                        className="flex-1 px-4 py-2.5 bg-navy-300 text-white rounded-lg text-sm font-medium cursor-not-allowed whitespace-nowrap opacity-60"
                      >
                        <i className="ri-lock-line mr-2" />
                        Clear Balance to Check-out
                      </button>
                    )}
                  </div>

                  {bill.pending > 0 && !isFullyPaid && (
                    <p className="text-xs text-red-500 text-center mt-3">
                      Full payment required before check-out. No balance can remain pending.
                    </p>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}